import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import MyBox from "./MyBox";

interface ShowOnOffFieldProps {
  label?: string;                    // Field label
  name: string;                      // Main field name in the form
  children: (value: any, setValue: (val: any) => void) => React.ReactNode; // Render prop for child field
  initialShow?: boolean;             // Initial visibility
  initialValue?: any;                // Initial form value
  dependentFields?: {                // Optional dependent fields to update automatically
    [fieldName: string]: (val: any) => any;
  };
  buttonClassName?: string;          // Optional button styling
  onText?: string;                   // Button text when shown
  offText?: string;                  // Button text when hidden
}

export function ShowOnOffField({
  label,
  name,
  children,
  initialShow = false,
  initialValue = null,
  dependentFields,
  buttonClassName = "",
  onText = "Hide",
  offText = "Show",
}: ShowOnOffFieldProps) {
  const { setValue, getValues } = useFormContext();
  const [isShown, setIsShown] = useState(initialShow);

  const currentValue = getValues(name) ?? initialValue;

  // Sync dependent fields whenever value changes
  const handleValueChange = (val: any) => {
    setValue(name, val, { shouldDirty: true });

    if (dependentFields) {
      Object.entries(dependentFields).forEach(([field, fn]) => {
        setValue(field, fn(val), { shouldDirty: true });
      });
    }
  };

  return (
    <MyBox topText={label}>
      <button
        type="button"
        onClick={() => setIsShown((prev) => !prev)}
        className={`px-4 py-2 rounded-xl text-sm font-medium transition hover:scale-105 ${buttonClassName} ${
          isShown
            ? "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
            : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
        }`}
      >
        {isShown ? onText : offText}
      </button>

      {isShown && <div className="mt-3">{children(currentValue, handleValueChange)}</div>}
    </MyBox>
  );
}